import { useCallback, useContext, useEffect, useRef } from 'react';
import { GlobalConfigurationContext } from '../../context/GlobalConfigurationContext';
import { identifyTools } from '../../../tools/Identity';
import { isVendorMode } from '../../Vendor';
import { UserRoleSchema, UserSchema } from '../../../definitions/schemas';
import Messenger from '../../../tools/Messenger';
import OrganizationsStore from '../../../stores/observables/OrganizationsStore';
import UserInterface from '../../../graphql/User/UserInterface';
import UserStore, {
  UserRolesStore,
} from '../../../stores/observables/UserStore';

const EMPTY_ORGANIZATION = '0';

interface Config {
  isExtensionInstalled: boolean;
}

const useOrganizationObserver = (config?: Config) => {
  const { userAuth } = useContext(GlobalConfigurationContext);

  const lastObservedOrganization = useRef('');

  useEffect(() => {
    OrganizationsStore.getCurrentObservable().subscribe(onOrganizationChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * If the organization context is set and there's a logged in user, a request
   * to fetch the member (the user's profile in the current organization)
   * information is performed, in order to have the user's vendorUserId,
   * because the first `me` request (which initially sets the context) is always
   * performed without an organization context, which leaves this member info
   * unavailable to properly identifying the user in the tools.
   */
  const resfreshUserData = useCallback(
    async (currentUser: UserSchema, userRoles?: UserRoleSchema[]) => {
      try {
        const { member, photo } = await new UserInterface().getMember();

        const extendedUser: UserSchema = {
          ...currentUser,
          member,
          photo,
          userRoles,
        };

        /**
         * Setting the user in the store again, this time with the member info,
         * so it's available without having to load the member. this is useful
         * as the analytics tools need to know about these values
         *
         * @see https://github.com/referenceable/referenceable_api/blob/develop/app/graphql/application/types/user_type.rb#L34-L39
         */
        UserStore.setExtended(extendedUser);
        return extendedUser;
      } catch (e) {
        // do nothing, as this doesn't concern the User
      }
    },
    []
  );

  /**
   * Load Intercom only if the app is not in vendor mode. This leaves it only
   * available for Standalone, which includes Room view page; in that case,
   * it's only available for orgs in Standalone mode.
   */
  const loadIntercom = useCallback(async () => {
    if (!isVendorMode()) {
      Messenger.register();
    }
  }, []);

  /**
   * @note Anonymous sessions never identify the user. These include: log in
   * and sign up pages and guests visiting rooms or providing references.
   * Nevertheless, analytics are still tracked because the tools are initialized
   * on the App's bootstrap.
   */
  const identifyAnalytics = useCallback(
    async (currentOrganization: string) => {
      let currentUser = UserStore.get();
      const userRoles = UserRolesStore.get();
      if (!(currentOrganization && currentUser)) {
        return;
      }

      try {
        // if there is not member information stored, do a 'me' call to pull the member data
        if (!currentUser.member) {
          currentUser = await resfreshUserData(currentUser, userRoles);
        }

        if (currentUser) {
          identifyTools(currentUser, userAuth, config?.isExtensionInstalled);
        }
      } catch (e) {
        // do nothing, as this doesn't concern the User
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, resfreshUserData]
  );

  const onOrganizationChange = useCallback(
    (currentOrg: string) => {
      if (
        currentOrg === EMPTY_ORGANIZATION ||
        currentOrg === lastObservedOrganization.current
      ) {
        return;
      }

      lastObservedOrganization.current = currentOrg;
      loadIntercom();
      identifyAnalytics(currentOrg);
    },
    [identifyAnalytics, loadIntercom]
  );
};

export default useOrganizationObserver;
